import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import Quill from 'quill';
import ImageResize from 'quill-image-resize-module-react';
import { ImageDrop } from 'quill-image-drop-module';
import htmlEditButton from 'quill-html-edit-button';
import 'quill/dist/quill.snow.css';

// Register third-party modules once at module scope.
Quill.register('modules/imageResize', ImageResize);
Quill.register('modules/imageDrop', ImageDrop);
Quill.register('modules/htmlEditButton', htmlEditButton);

function useLatestRef(value) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

const RichTextEditor = forwardRef(({ value, onChange, modules, formats, className }, ref) => {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const lastHtmlRef = useRef('');
  const onChangeRef = useLatestRef(onChange);
  const modulesRef = useLatestRef(modules);
  const formatsRef = useLatestRef(formats);

  useImperativeHandle(ref, () => ({
    getEditor: () => quillRef.current,
  }), []);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    // Prepare the editable element inside the container.
    const editorElement = document.createElement('div');
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(editorElement);

    const quill = new Quill(editorElement, {
      theme: 'snow',
      modules: modulesRef.current,
      formats: formatsRef.current,
    });

    quillRef.current = quill;

    if (value) {
      quill.root.innerHTML = value;
      lastHtmlRef.current = value;
    }

    const handleTextChange = () => {
      const html = quill.root.innerHTML;
      lastHtmlRef.current = html;
      if (onChangeRef.current) {
        onChangeRef.current(html);
      }
    };

    quill.on('text-change', handleTextChange);

    return () => {
      quill.off('text-change', handleTextChange);
      quillRef.current = null;
      containerRef.current?.replaceChildren();
    };
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    const normalizedValue = value || '';
    if (normalizedValue === lastHtmlRef.current) return;

    const selection = quill.getSelection();
    quill.root.innerHTML = normalizedValue;
    lastHtmlRef.current = normalizedValue;
    if (selection) {
      quill.setSelection(selection);
    }
  }, [value]);

  return <div className={className} ref={containerRef} />;
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
